import { memo, useRef, useState } from 'react';
import { Bot, Check, CheckCheck, CornerUpRight, FileText, MapPin, RotateCw, StickyNote, X } from 'lucide-react';
import type { CwAttachment, UiMessage } from '@/api/inboxTypes';
import type { BotDecision } from '@/api/bots';
import { proposalOf, type BotProposal } from '@/lib/botProposal';
import { colleagueName, formatBytes, formatClock, type BubbleKind } from '@/lib/messageModel';
import { forwardedNote } from '@/lib/forward';
import { AudioPlayer } from './AudioPlayer';

interface Props {
  message: UiMessage;
  kind: BubbleKind;
  myUserId: number | null;
  onRetry: (echoId: string) => void;
  onDiscard: (echoId: string) => void;
  onDecide: (invocationId: number, decision: BotDecision) => Promise<void>;
  /** Режим выбора для пересылки. */
  selectMode?: boolean;
  /** Это сообщение можно переслать (иначе в режиме выбора оно затемнено и не отмечается). */
  selectable?: boolean;
  selected?: boolean;
  /** Долгое нажатие — войти в режим выбора с этим сообщением. */
  onLongPress?: (id: number) => void;
  /** Нажатие в режиме выбора — поставить/снять отметку. */
  onToggle?: (id: number) => void;
}

/** Сколько держать палец, чтобы это считалось долгим нажатием. */
const LONG_PRESS_MS = 450;
/** Сдвиг пальца, после которого это уже прокрутка, а не долгое нажатие. */
const LONG_PRESS_SLOP = 10;

/** Имя файла из адреса вложения (Chatwoot отдаёт его только в пути). */
function fileName(a: CwAttachment): string {
  const url = a.data_url ?? '';
  try {
    const last = decodeURIComponent(new URL(url).pathname.split('/').filter(Boolean).pop() ?? '');
    if (last) return last;
  } catch { /* не адрес */ }
  return a.fallback_title || (a.extension ? `файл.${a.extension}` : 'Файл');
}

function Attachment({ a }: { a: CwAttachment }) {
  switch (a.file_type) {
    case 'image':
      return (
        <a href={a.data_url} target="_blank" rel="noopener noreferrer" aria-label="Открыть фото">
          <img className="thumb" src={a.thumb_url || a.data_url} alt="Фото" loading="lazy" />
        </a>
      );
    case 'audio':
      return <AudioPlayer att={a} />;
    case 'location':
      return (
        <a className="fchip" href={`https://maps.google.com/?q=${a.coordinates_lat},${a.coordinates_long}`} target="_blank" rel="noopener noreferrer">
          <MapPin aria-hidden /><span>{a.fallback_title || 'Геопозиция'}</span>
        </a>
      );
    case 'video':
    case 'file':
    default:
      return (
        <a className="fchip" href={a.data_url} target="_blank" rel="noopener noreferrer" download>
          <FileText aria-hidden /><span>{a.file_type === 'video' ? 'Видео' : fileName(a)}</span>
          {a.file_size ? <small className="tnum">{formatBytes(a.file_size)}</small> : null}
        </a>
      );
  }
}

/** Кнопки решения под предложением бота. Показываем итог, как только человек нажал, и откатываем при отказе сервера. */
function ProposalActions({ proposal, onDecide }: { proposal: BotProposal; onDecide: Props['onDecide'] }) {
  const [busy, setBusy] = useState<BotDecision | null>(null);
  const [local, setLocal] = useState<'confirmed' | 'cancelled' | null>(null);
  const state = local ?? proposal.state;
  const decide = async (d: BotDecision) => {
    setBusy(d);
    try {
      await onDecide(proposal.invocationId, d);
      setLocal(d === 'confirm' ? 'confirmed' : 'cancelled');
    } catch {
      /* сообщение об ошибке показывает вызывающий; кнопки остаются */
    } finally {
      setBusy(null);
    }
  };
  if (state !== 'pending') {
    return (
      <small>
        {state === 'confirmed' ? 'Подтверждено' : 'Отклонено'}{proposal.decidedBy ? `: ${proposal.decidedBy}` : ''}
      </small>
    );
  }
  return (
    <div className="acts">
      <button type="button" className="pri" disabled={busy !== null} onClick={() => void decide('confirm')}>
        {proposal.confirmLabel || 'Подтвердить'}
      </button>
      <button type="button" disabled={busy !== null} onClick={() => void decide('cancel')}>Отклонить</button>
    </div>
  );
}

/** Пузырь сообщения: они / я / заметка / бот; служебное — строка по центру. */
export const MessageBubble = memo(function MessageBubble({
  message: m, kind, myUserId, onRetry, onDiscard, onDecide, selectMode, selectable, selected, onLongPress, onToggle,
}: Props) {
  const press = useRef<{ x: number; y: number; timer: ReturnType<typeof setTimeout> } | null>(null);
  /** Долгое нажатие сработало — следующий «клик» того же касания не должен снять отметку. */
  const fired = useRef(false);
  const cancelPress = () => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
  };
  if (kind === 'sys') {
    return <span className="sys">{m.content}</span>;
  }
  const colleague = kind === 'me' ? colleagueName(m, myUserId) : null;
  const failed = m.local === 'failed' || m.status === 'failed';
  const own = kind === 'me' || kind === 'note' || kind === 'bot';
  const proposal = kind === 'bot' ? proposalOf(m) : null;

  let status: React.ReactNode = null;
  if (own) {
    if (m.local === 'sending') status = ' отправляется';
    else if (m.local === 'failed') status = null;
    else if (m.status === 'failed') status = ' не доставлено';
    else if (kind !== 'note') status = m.status === 'delivered' || m.status === 'read' ? <CheckCheck aria-label="Доставлено" /> : <Check aria-label="Отправлено" />;
  }

  const note = forwardedNote(m);

  const onPointerDown = (e: React.PointerEvent) => {
    if (selectMode || !selectable || !onLongPress || e.button > 0) return;
    cancelPress();
    fired.current = false;
    press.current = {
      x: e.clientX, y: e.clientY,
      timer: setTimeout(() => { press.current = null; fired.current = true; onLongPress(m.id); }, LONG_PRESS_MS),
    };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const p = press.current;
    if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > LONG_PRESS_SLOP) cancelPress();
  };
  // В режиме выбора пузырь — переключатель: ссылки и кнопки внутри не срабатывают.
  const onClickCapture = (e: React.MouseEvent) => {
    if (fired.current) { fired.current = false; e.preventDefault(); e.stopPropagation(); return; }
    if (!selectMode) return;
    e.preventDefault();
    e.stopPropagation();
    if (selectable) onToggle?.(m.id);
  };

  return (
    <div
      className={`b ${kind}${failed ? ' failed' : ''}${selectMode ? ` sel${selectable ? '' : ' nosel'}${selected ? ' on' : ''}` : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={cancelPress}
      onPointerCancel={cancelPress}
      onPointerLeave={cancelPress}
      onContextMenu={selectable || selectMode ? (e) => e.preventDefault() : undefined}
      onClickCapture={onClickCapture}
      aria-pressed={selectMode && selectable ? !!selected : undefined}
    >
      {selectMode && selectable && (
        <span className="bchk" aria-hidden>{selected ? <Check /> : null}</span>
      )}
      {note && <span className="fwd"><CornerUpRight aria-hidden />{note}</span>}
      {kind === 'note' && <span className="lbl"><StickyNote aria-hidden />Заметка, клиент не видит</span>}
      {kind === 'bot' && <span className="lbl"><Bot aria-hidden />{m.sender?.name || 'Бот'}</span>}
      {colleague && <span className="lbl">{colleague}</span>}
      {(m.attachments ?? []).map((a) => <Attachment key={a.id} a={a} />)}
      {proposal && <b className="ptitle">{proposal.title}</b>}
      {m.content ? <span>{m.content}</span> : null}
      {proposal && proposal.details.length > 0 && (
        <span className="pdet">{proposal.details.map(([k, v]) => <span key={k}><em>{k}</em> {v}</span>)}</span>
      )}
      {proposal && <ProposalActions proposal={proposal} onDecide={onDecide} />}
      <small>
        {formatClock(m.created_at)}
        {status}
      </small>
      {m.local === 'failed' && m.echo_id && (
        <small>
          <span role="alert">{m.localError || 'Не отправлено'}</span>
          <button type="button" className="retry" onClick={() => onRetry(m.echo_id!)} aria-label="Повторить отправку">
            <RotateCw aria-hidden />Повторить
          </button>
          <button type="button" className="retry" onClick={() => onDiscard(m.echo_id!)} aria-label="Убрать сообщение">
            <X aria-hidden />
          </button>
        </small>
      )}
    </div>
  );
});
